import { useMutation } from '@tanstack/react-query'
import { ExternalLink } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { postJSON } from '@/lib/api'
import type { PlatformAssociationDetail } from './PlatformAssociationDetailPage'

export interface DemoPersona {
  kind: 'staff' | 'roster_manager' | 'end_user'
  name: string
  role_caption: string
}

interface DemoSignInResponse {
  url: string
  expires_at: string
}

const personaLabels = {
  staff: 'Staff',
  roster_manager: 'Roster manager',
  end_user: 'End user',
} as const

// Presentation copy for the mint endpoint's refusal codes; any other server message shows verbatim.
const refusalCopy: Record<string, string> = {
  demo_association_not_seeded: 'This demo association has not finished seeding.',
  demo_association_required: 'Only demo associations offer demo sign-ins.',
}

function refusalMessage(error: Error) {
  return refusalCopy[error.message] ?? error.message
}

export function PlatformDemoSignIns({ association }: { association: PlatformAssociationDetail }) {
  const signIn = useMutation({
    mutationFn: (persona: DemoPersona['kind']) =>
      postJSON<DemoSignInResponse>(
        `/api/v1/platform/associations/${association.id}/demo_sign_ins`,
        { persona },
      ),
    onSuccess: ({ url }) => {
      window.open(url, '_blank', 'noopener,noreferrer')
    },
  })

  return (
    <Card className="mb-5 max-w-2xl" role="region" aria-labelledby="demo-sign-ins-title">
      <CardHeader className="p-4 pb-3">
        <CardTitle id="demo-sign-ins-title" className="text-body font-semibold">
          Demo sign-ins
        </CardTitle>
        <CardDescription>
          Opens the association site in a new tab, signed in. Links expire after five minutes.
        </CardDescription>
      </CardHeader>
      <CardContent className="divide-y divide-border border-t border-border p-0">
        {(association.demo_personas ?? []).map((persona) => (
          <div key={persona.kind} className="flex items-center justify-between gap-3 px-4 py-2">
            <div className="flex min-w-0 flex-col">
              <span className="text-body font-semibold text-foreground">
                {personaLabels[persona.kind]}
              </span>
              <span className="text-caption text-muted-foreground">
                {persona.name} · {persona.role_caption}
              </span>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="shrink-0"
              disabled={association.seed_status !== 'seeded' || signIn.isPending}
              onClick={() => signIn.mutate(persona.kind)}
              aria-label={`Sign in as ${personaLabels[persona.kind]}`}
            >
              Sign in as
              <ExternalLink className="ml-1.5 h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        ))}
        {signIn.error && (
          <Alert className="rounded-none border-0 px-4 py-3">{refusalMessage(signIn.error)}</Alert>
        )}
      </CardContent>
    </Card>
  )
}
